import { Box, Button, TextField, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import {
  sendParameters,
  sendParametersForEdit
} from '../../../common/functions'
import FileUpload from '../../../components/file/FileUpload'
import GetBillFrom1cButton from './GetBillFrom1cButton'
import TaskWrapper from './TaskWrapper'
import { IMaskInput } from 'react-imask'
import BillScanLink from '../../../components/BillScanLink'
import { TASK_TYPE } from '../../../common/constants'
import FileViewWidget from '../../../components/file/FileView'

interface TextMaskCustomProps {
  name: string
  onChange: (event: { target: { name: string; value: string } }) => void
  [key: string]: any
}

const TextMaskCustom = React.forwardRef<HTMLElement, TextMaskCustomProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props
    return (
      <IMaskInput
        {...other}
        mask="00БП-000000"
        inputRef={ref as any}
        onAccept={(value) =>
          onChange({ target: { name: props.name, value: value as string } })
        }
        overwrite
      />
    )
  }
)

interface InitialBillingWidgetProps {
  task: any
  isCanceled: boolean
}

export default function InitialBillingWidget({
  task,
  isCanceled
}: InitialBillingWidgetProps) {
  const [billNumber, setBillNumber] = React.useState(
    task.meta?.billNumber || ''
  )
  const [editMode, setEditMode] = React.useState(!task.meta?.billNumber)
  const [billDate, setBillDate] = React.useState<any>(
    !!task.meta?.billDate ? dayjs(task.meta?.billDate) : dayjs()
  )
  const [billSentDate, setBillSendDate] = React.useState<any>(
    !!task.meta?.billSentDate ? dayjs(task.meta?.billSentDate) : dayjs()
  )
  const [files, setFiles] = React.useState<any[]>([])
  let enabled = task.isActive && editMode && task?.accessFlags?.update
  // Если пользователь меняет номер или дату счёта, ранее полученный из 1С счёт
  // перестаёт быть актуальным — сбрасываем флаг, чтобы можно было получить новый.
  const [hasBillFrom1c, setHasBillFrom1c] = React.useState(false)
  const isBillPaid = task.meta?.isBillPaid || false

  const deadline = 30
  const now = dayjs()
  const timer = () => {
    if (!billSentDate.add) return
    const deadlineDate = billSentDate?.add(deadline, 'day')
    const diff = deadlineDate.diff(now, 'day')
    if (diff < 0) {
      return Math.abs(diff) + ' дней'
    } else {
      return diff + ' дней'
    }
  }
  useEffect(() => {
    timer()
  }, [])

  useEffect(() => {
    if (!task.meta?.billNumber) return
    setEditMode(false)
  }, [task])

  useEffect(() => {
    setHasBillFrom1c(false)
  }, [billNumber, billDate])

  function closeTask(id: number) {}
  return (
    <TaskWrapper
      task={task}
      isCanceled={isCanceled}
      label="Отдел бух. учёта"
      disabled={!enabled}
      rightContent={
        task.isActive &&
        !editMode && (
          <Button size="small" onClick={() => setEditMode(true)}>
            Редактировать
          </Button>
        )
      }
      onSave={() => {
        if (!task.meta?.billNumber) {
          sendParameters(
            task.paidServiceId,
            task.id,
            {
              billNumber,
              billDate: billDate?.format
                ? billDate.format('YYYY-MM-DD')
                : billDate,
              billSentDate: billSentDate?.format
                ? billSentDate.format('YYYY-MM-DD')
                : billSentDate
            },
            files
          )
        } else {
          sendParametersForEdit(
            task.paidServiceId,
            task.id,
            {
              billNumber,
              billDate: billDate?.format
                ? billDate.format('YYYY-MM-DD')
                : billDate,
              billSentDate: billSentDate?.format
                ? billSentDate.format('YYYY-MM-DD')
                : billSentDate
            },
            files
          )
        }
      }}
    >
      <Box display="flex" flexDirection="column">
        <Typography variant="subtitle1" color="black">
          Счёт:
        </Typography>
        <Box display="flex" justifyContent="space-between" gap={1}>
          <TextField
            placeholder={'00БП-000000'}
            variant="outlined"
            disabled={!enabled}
            label="Номер"
            value={billNumber}
            InputProps={{
              inputComponent: TextMaskCustom as any
            }}
            onChange={(e) => setBillNumber(e.target.value)}
            required
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Дата"
              inputFormat="DD.MM.YYYY"
              disabled={!enabled}
              value={billDate}
              onChange={(newValue) => {
                setBillDate(newValue?.format('YYYY-MM-DD'))
              }}
              renderInput={(params) => <TextField required {...params} />}
            />
          </LocalizationProvider>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column">
        <Typography variant="subtitle1" color="black">
          Дата отправки счёта:
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disabled={!enabled}
            inputFormat="DD.MM.YYYY"
            value={billSentDate}
            onChange={(newValue) => {
              setBillSendDate(newValue?.format('YYYY-MM-DD'))
            }}
            renderInput={(params) => <TextField required {...params} />}
          />
        </LocalizationProvider>
      </Box>
      {
        <Typography variant="subtitle1">
          Статус счета : {task.status ? task.status : 'нет данных'}
        </Typography>
      }
      {!enabled && (
        <>
          {!isBillPaid && (
            <Typography
              variant="subtitle1"
              color={isBillPaid ? 'green' : 'red'}
            >
              Ожидаемый срок {timer()}
            </Typography>
          )}
          {task.payment_deadline_is_over && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <span>Срок истек</span>
              <Button
                variant="contained"
                color="error"
                onClick={() => closeTask(task)}
              >
                Закрыть услугу
              </Button>
            </Box>
          )}
        </>
      )}

      {task.meta?.isBillPaid === false && (
        <Box display="flex" flexDirection="column" gap={1}>
          <BillScanLink
            fileId={task.meta?.billFileId}
            taskId={task.id}
            taskTypeId={TASK_TYPE.INITIAL_BILLING}
            billNumber={task.meta?.billNumber}
            billDate={task.meta?.billDate}
            label="Скан счёта"
          />
        </Box>
      )}
      {task?.isActive && !task?.payment_deadline_is_over && (
        <>
          <FileUpload
            files={files}
            onUpload={(file: any) => setFiles([...files, file])}
            onDelete={(id: any) =>
              setFiles(files.filter((file: any) => file.id !== id))
            }
          />
          {!task.meta?.billFileId && files.length === 0 && (
            <GetBillFrom1cButton
              number={billNumber}
              date={billDate}
              disabled={hasBillFrom1c}
              onSuccess={() => setHasBillFrom1c(true)}
            />
          )}
          {hasBillFrom1c && (
            <Typography color="green">Счёт из 1С получен</Typography>
          )}
        </>
      )}

      <FileViewWidget files={task.files} />
    </TaskWrapper>
  )
}
